import type { ReactNode } from 'react';

import { ANALYSIS_STAGES } from '@mouja/domain/analysis';

import type {
  AnalysisDay2State,
  AnalysisDay3State,
  AnalysisDay4State,
  AnalysisDay5State,
  AnalysisDay6State,
  AnalysisDay7State,
  AnalysisDay8State,
  AnalysisRunSummary,
} from '../api.js';
import { InlineAlert, Tag } from '../ui.js';

function object(value: unknown): Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Readonly<Record<string, unknown>>)
    : {};
}

function text(value: unknown, fallback = '—'): string {
  return typeof value === 'string' && value.trim() !== '' ? value : fallback;
}

function strings(value: unknown): readonly string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : [];
}

function number(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function dollars(micros: unknown): string {
  const parsed = number(micros);
  return parsed === null ? 'Unknown' : `$${(parsed / 1_000_000).toFixed(4)}`;
}

const STAGE_LABELS: Readonly<Record<string, string>> = {
  INTAKE: 'Intake',
  BUSINESS_EVIDENCE: 'Business evidence',
  BUSINESS_PROFILE_REVIEW: 'Business Profile review',
  COMPETITOR_DISCOVERY: 'Competitor discovery',
  COMPETITOR_CONFIRMATION: 'Competitor confirmation',
  TARGET_AND_COMPETITOR_CONTENT_COLLECTION: 'Content collection',
  SHALLOW_CONTENT_ENRICHMENT: 'Structured content',
  DEEP_CONTENT_UNDERSTANDING: 'Reviewed understanding',
  AUDIENCE_SIGNAL_EXTRACTION: 'Audience evidence',
  COMPARATIVE_ANALYSIS: 'Comparison',
  FINDING_CANDIDATES: 'Candidate findings',
  FINDINGS_REVIEW: 'Findings review',
  STRATEGIC_OPPORTUNITIES: 'Target-fit opportunities',
  CREATIVE_DIRECTIONS: 'Directions',
  COMPLETE: 'Strategy Brief',
};

const OWNER_STAGES = new Set([
  'BUSINESS_PROFILE_REVIEW',
  'COMPETITOR_CONFIRMATION',
  'DEEP_CONTENT_UNDERSTANDING',
  'AUDIENCE_SIGNAL_EXTRACTION',
  'FINDINGS_REVIEW',
  'STRATEGIC_OPPORTUNITIES',
  'CREATIVE_DIRECTIONS',
]);

export function AnalysisPipeline({
  run,
}: {
  readonly run: AnalysisRunSummary;
}): ReactNode {
  const current = Math.max(
    0,
    ANALYSIS_STAGES.findIndex((stage) => stage === run.currentStage),
  );
  return (
    <section className="mj-analysis-pipeline" aria-labelledby="analysis-pipeline-title">
      <div className="mj-analysis-section-heading">
        <div>
          <p className="mj-analysis-eyebrow">Evidence-to-decision path</p>
          <h2 id="analysis-pipeline-title">How this conclusion was reached</h2>
        </div>
        <div className="mj-analysis-legend" aria-label="Processing legend">
          <span>
            <i data-kind="automated" /> Automated / deterministic
          </span>
          <span>
            <i data-kind="reviewed" /> Owner-assisted / reviewed
          </span>
        </div>
      </div>
      <ol>
        {ANALYSIS_STAGES.map((stage, index) => {
          const state =
            index < current ? 'complete' : index === current ? 'current' : 'future';
          return (
            <li
              key={stage}
              data-state={state}
              data-kind={OWNER_STAGES.has(stage) ? 'reviewed' : 'automated'}
            >
              <span className="mj-analysis-stage-index">
                {index < current ? '✓' : index + 1}
              </span>
              <span>{STAGE_LABELS[stage] ?? stage.replaceAll('_', ' ')}</span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

interface BriefProps {
  readonly run: AnalysisRunSummary;
  readonly day2: AnalysisDay2State;
  readonly day3: AnalysisDay3State;
  readonly day4: AnalysisDay4State;
  readonly day5: AnalysisDay5State;
  readonly day6: AnalysisDay6State;
  readonly day7: AnalysisDay7State;
  readonly day8: AnalysisDay8State;
}

export function AnalysisStrategyBrief({
  run,
  day2,
  day3,
  day4,
  day5,
  day6,
  day7,
  day8,
}: BriefProps): ReactNode {
  const artifact = day8.strategyBrief;
  if (artifact === null) return null;
  const brief = artifact.payload;
  const business = object(brief['business']);
  const offer = object(brief['offer']);
  const costs = object(brief['costs']);
  const interventions = object(brief['manualInterventions']);
  const outcome = text(brief['analyticalOutcome'], 'UNKNOWN');
  const supported = outcome === 'SUPPORTED';
  const finding = day7.approvedFindings[0] ?? null;
  const opportunity = day8.approvedOpportunities[0] ?? null;
  const direction = day8.approvedCreativeDirections[0] ?? null;
  const directionPayload = direction?.payload ?? {};
  const opportunityId = text(directionPayload['approvedOpportunityArtifactId'], '');
  const linkedOpportunity =
    day8.approvedOpportunities.find((entry) => entry.id === opportunityId) ??
    opportunity;
  const findingIds = strings(directionPayload['supportingFindingArtifactIds']);
  const linkedFinding =
    day7.approvedFindings.find((entry) => findingIds.includes(entry.id)) ?? finding;
  const support = object(linkedFinding?.payload['support']);
  const postIds = strings(support['postIds']);
  const sourcePost = day4.content.find((entry) => postIds.includes(entry.id)) ?? null;
  const deepFeature = sourcePost
    ? (day5.features.find(
        (entry) =>
          entry.contentItemId === sourcePost.id && entry.enrichmentTier === 'DEEP',
      ) ?? null)
    : null;
  const audienceSignal = sourcePost
    ? (day6.signals.find((entry) => entry.contentItemId === sourcePost.id) ?? null)
    : null;
  const confirmedCompetitors = day3.candidates.filter(
    (entry) => entry.state === 'CONFIRMED',
  );
  const reviewedContent = day5.selections.length;
  const sampledComments = day4.commentSamples.reduce(
    (total, sample) => total + sample.comments.length,
    0,
  );
  const usesClosedFixtureTransport = day2.acquisitions.some((acquisition) =>
    acquisition.providerRunId?.startsWith('fixture-'),
  );
  const limitations = [
    ...strings(brief['limitations']),
    'This is a bounded cohort, not market-wide research.',
    'Comments are sampled and do not represent every viewer.',
    'The canonical corpus is Owner-imported and Tier-2 understanding is Owner-reviewed.',
    'Observed associations are not causal claims or performance predictions.',
  ].filter((entry, index, entries) => entries.indexOf(entry) === index);
  const byCategory = object(interventions['byCategory']);
  const profile = day2.approvedProfile?.payload ?? null;
  const targetContext = profile
    ? [profile.niche.value, profile.geography.value, profile.positioning.value]
        .filter((value): value is string => value !== null && value.trim() !== '')
        .join(' · ')
    : '';

  return (
    <section
      id="strategy-brief"
      className="mj-strategy-brief"
      aria-labelledby="strategy-brief-title"
    >
      <header className="mj-strategy-brief-header">
        <div>
          <p className="mj-analysis-eyebrow">Immutable strategy-brief.v1</p>
          <h2 id="strategy-brief-title">Strategy Brief</h2>
          <p>
            {text(business['name'], run.targetBusinessName)} ·{' '}
            {text(offer['title'], run.primaryOfferTitle ?? 'Selected Offer')}
          </p>
          {targetContext ? <p dir="auto">{targetContext}</p> : null}
        </div>
        <Tag tone={supported ? 'success' : 'warning'}>
          {supported ? 'SUPPORTED' : 'NOT ENOUGH EVIDENCE TO RECOMMEND ACTION'}
        </Tag>
      </header>

      {!supported ? (
        <InlineAlert
          tone="warning"
          title="The analysis completed successfully, but the evidence did not support a defensible recommendation."
        >
          Mouja preserved the available evidence and stopped before manufacturing a
          finding. More comparable content, clearer Offer evidence, or a broader
          reviewed sample may help.
        </InlineAlert>
      ) : null}

      {usesClosedFixtureTransport ? (
        <InlineAlert tone="info" title="Canonical demo provenance">
          Business-source acquisition used a closed provider-shaped fixture transport.
          Adapter names describe the exercised contract, not a live provider run. Social
          evidence is Owner-imported and reviewed.
        </InlineAlert>
      ) : null}

      <dl className="mj-analysis-scope" aria-label="Analysis scope">
        <div>
          <dt>Competitors</dt>
          <dd>{confirmedCompetitors.length}</dd>
        </div>
        <div>
          <dt>Content sample</dt>
          <dd>{day4.content.length}</dd>
        </div>
        <div>
          <dt>Deeply reviewed</dt>
          <dd>{reviewedContent}</dd>
        </div>
        <div>
          <dt>Sampled comments</dt>
          <dd>{sampledComments}</dd>
        </div>
        <div>
          <dt>Cutoff</dt>
          <dd>
            {text(
              object(brief['analysisScope'])['metricCutoff'],
              'Recorded in evidence',
            )}
          </dd>
        </div>
      </dl>

      <div className="mj-strategy-grid">
        <article id="approved-findings" className="mj-analysis-conclusion-card">
          <p className="mj-analysis-eyebrow">What Mouja found</p>
          {linkedFinding ? (
            <>
              <h3>{text(linkedFinding.payload['wording'], 'Approved finding')}</h3>
              <div className="mj-analysis-proof-numbers">
                <span>
                  <strong>{String(number(support['postCount']) ?? 0)}</strong> reviewed
                  posts
                </span>
                <span>
                  <strong>{String(number(support['businessCount']) ?? 0)}</strong>{' '}
                  businesses represented
                </span>
                <span>
                  <strong>
                    {text(linkedFinding.payload['evidenceStrength'], 'Bounded')}
                  </strong>{' '}
                  evidence
                </span>
              </div>
              <p>
                {strings(linkedFinding.payload['limitations'])[0] ??
                  'Applies only to this run and reviewed cohort.'}
              </p>
            </>
          ) : (
            <p>No candidate claim survived support and counter-evidence review.</p>
          )}
        </article>

        <article id="approved-opportunities" className="mj-analysis-conclusion-card">
          <p className="mj-analysis-eyebrow">Why it matters</p>
          {linkedOpportunity ? (
            <>
              <h3>
                {text(
                  linkedOpportunity.payload['finalWording'],
                  'Approved opportunity',
                )}
              </h3>
              <p>
                {strings(linkedOpportunity.payload['targetRelevanceBasis'])[0] ??
                  text(
                    object(linkedOpportunity.payload['targetEvidence'])['basis'],
                    'Target fit was reviewed by the Owner.',
                  )}
              </p>
              <p>
                <strong>Feasibility:</strong>{' '}
                {text(
                  object(linkedOpportunity.payload['feasibility'])['state'],
                  'Unknown',
                )}
              </p>
            </>
          ) : (
            <p>
              No opportunity was allowed through because no approved finding supported
              one.
            </p>
          )}
        </article>

        <article
          id="approved-directions"
          className="mj-analysis-conclusion-card mj-analysis-direction-card"
        >
          <p className="mj-analysis-eyebrow">What to test</p>
          {direction ? (
            <>
              <h3>{text(directionPayload['title'], 'Creative direction')}</h3>
              <dl>
                <div>
                  <dt>Objective</dt>
                  <dd>{text(directionPayload['objective'])}</dd>
                </div>
                <div>
                  <dt>Mechanism</dt>
                  <dd>{text(directionPayload['coreMechanism'])}</dd>
                </div>
                <div>
                  <dt>Angle</dt>
                  <dd>{text(directionPayload['angle'])}</dd>
                </div>
                <div>
                  <dt>Proof</dt>
                  <dd>{text(directionPayload['proofStructure'])}</dd>
                </div>
                <div>
                  <dt>Offer</dt>
                  <dd>{text(object(directionPayload['offer'])['title'])}</dd>
                </div>
                <div>
                  <dt>CTA direction</dt>
                  <dd>{text(directionPayload['ctaDirection'], 'None')}</dd>
                </div>
              </dl>
              {text(directionPayload['optionalExampleHook'], '') ? (
                <blockquote dir="auto">
                  {text(directionPayload['optionalExampleHook'])}
                </blockquote>
              ) : null}
            </>
          ) : (
            <p>No direction was generated. This is the intended restrained outcome.</p>
          )}
        </article>
      </div>

      {direction && linkedOpportunity && linkedFinding ? (
        <details id="evidence-chain" className="mj-evidence-chain">
          <summary>View the direction-to-source evidence chain</summary>
          <ol>
            <li>
              <strong>Direction</strong>
              <span>{text(directionPayload['title'])}</span>
              <code>{direction.id}</code>
            </li>
            <li>
              <strong>Supporting opportunity</strong>
              <span>{text(linkedOpportunity.payload['finalWording'])}</span>
              <code>{linkedOpportunity.id}</code>
            </li>
            <li>
              <strong>Approved finding</strong>
              <span>{text(linkedFinding.payload['wording'])}</span>
              <code>{linkedFinding.id}</code>
            </li>
            <li>
              <strong>Deterministic comparison</strong>
              <span>
                Observed content-performance association within the bounded cohort.
              </span>
              <code>{day6.comparativeAnalysis?.id ?? 'Unavailable'}</code>
            </li>
            {sourcePost ? (
              <li>
                <strong>Specific observed post</strong>
                <span dir="auto">{sourcePost.caption ?? 'Caption unavailable'}</span>
                <a href={sourcePost.canonicalUrl} target="_blank" rel="noreferrer">
                  Open original source
                </a>
              </li>
            ) : null}
            {deepFeature ? (
              <li>
                <strong>Reviewed content understanding</strong>
                <span>
                  Owner-reviewed Tier-2 feature evidence, version{' '}
                  {deepFeature.featureVersion}.
                </span>
                <code>{deepFeature.artifactId}</code>
              </li>
            ) : null}
            {audienceSignal ? (
              <li>
                <strong>Sampled audience evidence</strong>
                <span dir="auto">{audienceSignal.originalText}</span>
                <code>{audienceSignal.artifactId}</code>
              </li>
            ) : null}
          </ol>
        </details>
      ) : null}

      <details id="limitations" className="mj-analysis-secondary">
        <summary>Limitations, provenance, cost and operator effort</summary>
        <div className="mj-analysis-secondary-grid">
          <section>
            <h3>Limitations</h3>
            <ul>
              {limitations.map((limitation) => (
                <li key={limitation}>{limitation}</li>
              ))}
            </ul>
          </section>
          <section>
            <h3>Cost basis</h3>
            <dl>
              <div>
                <dt>Exact fixture spend</dt>
                <dd>{dollars(costs['exactCostMicros'])}</dd>
              </div>
              <div>
                <dt>Modeled provider cost</dt>
                <dd>{dollars(costs['estimatedCostMicros'])}</dd>
              </div>
              <div>
                <dt>Unknown/unvalidated records</dt>
                <dd>{String(number(costs['unknownCostRecords']) ?? 0)}</dd>
              </div>
            </dl>
          </section>
          <section>
            <h3>Owner effort</h3>
            <p>
              <strong>{String(number(interventions['count']) ?? 0)}</strong> recorded
              review/correction actions. Active human minutes: <strong>UNKNOWN</strong>.
            </p>
            <p>
              {Object.keys(byCategory).length} intervention categories are preserved in
              the immutable brief.
            </p>
          </section>
          <section>
            <h3>Provenance</h3>
            <p>
              Canonical social evidence: <strong>OWNER_IMPORTED</strong>.
            </p>
            <p>
              Deep understanding: <strong>OWNER_ANNOTATED / OWNER_REVIEWED</strong>.
            </p>
            <p>Live provider paths remain supplementary and are not implied here.</p>
          </section>
        </div>
      </details>

      <details className="mj-analysis-technical">
        <summary>Technical artifact details</summary>
        <p>
          Artifact {artifact.id} · immutable hash <code>{artifact.contentHash}</code>
        </p>
        <pre>{JSON.stringify(brief, null, 2)}</pre>
      </details>
    </section>
  );
}
